import { getFilterOptionsFromClassifier, mapToFilterOptions } from 'components/tables/list-table/filterUtil';
import { MainFilter } from 'components/tables/list-table/ListTableTypes';
import { useLazyGetMinContactsListWithFiltersQuery } from 'store/api/contacts';
import {
    useLazyGetAllEmployeesListWithFiltersQuery,
    useLazyGetEmployeesListWithFiltersQuery,
} from 'store/api/employees';
import { useLazyGetMinGroupsListWithFiltersQuery } from 'store/api/groups';
import { useLazyGetMinOfficesWithFiltersQuery } from 'store/api/offices';
import { useLazyGetMinTeamsListWithFiltersQuery } from 'store/api/teams';
import { ClassifierData } from 'types/classifiers';
import { PartyType } from 'types/contacts';
import { TeamType } from 'types/teams';
import { t } from 'utils/getTranslations';
import { officeInactiveSuffix } from 'utils/officeOptionRenderer';

type Deps = {
    classifiers: Record<string, ClassifierData | undefined>;
    relationshipTypeOptions: ReturnType<typeof mapToFilterOptions>;
    /** The name of the firm's configured last-effort type; its two filters read it. */
    activityType?: string;
    /** The Client assistant filter's label, under the name the firm gives the field. */
    clientAssistantLabel: string;
};

/**
 * The Contacts list's filters, as each list of persons that offers one of them reads it: the marketing profile's
 * panel takes them from here, so the two offer the same controls. Each list keeps its own gates (a feature flag,
 * the offices a firm runs, a configured last-effort type) and places them in its own groups.
 */
export const contactFilters = ({
    classifiers,
    relationshipTypeOptions,
    activityType = '',
    clientAssistantLabel,
}: Deps) =>
    ({
        myClients: {
            type: 'checkboxgroup',
            selector: 'id',
            comperator: '=ismyclient=',
            group: 'contacts.contact',
            filters: [
                {
                    tooltip: 'contacts.tooltips.my-clients',
                    label: t('contacts.my-clients'),
                    argument: 'true',
                },
            ],
        },
        myContacts: {
            type: 'checkboxgroup',
            selector: 'statusCode',
            comperator: '=ismycontact=',
            group: 'contacts.contact',
            filters: [
                {
                    tooltip: 'contacts.tooltips.my-contacts',
                    label: t('contacts.my-contacts'),
                    argument: 'true',
                },
            ],
        },
        name: {
            type: 'text',
            label: t('contacts.filter.contact-name'),
            group: 'contacts.contact',
            selector: 'name',
            multiple: true,
            comperator: '=contactnamelk=',
            tooltip: 'contacts.tooltips.contact-name-filter',
        },
        relationshipType: {
            type: 'checkboxgroup',
            label: t('contacts.about.relationship-type'),
            selector: 'relationshipTypeCode',
            group: 'contacts.contact',
            multiple: true,
            filters: relationshipTypeOptions,
        },
        internalClient: {
            type: 'checkboxgroup',
            label: t('contacts.filter.internal-client'),
            selector: 'isInternalClient',
            group: 'contacts.contact',
            multiple: false,
            filters: [
                {
                    label: t('yes'),
                    argument: 'true',
                },
                {
                    label: t('no'),
                    argument: 'false',
                },
            ],
        },
        invoiceDateCurrentDate: {
            type: 'checkboxgroup',
            label: t('contacts.filter.invoice-date-current-date'),
            selector: 'issueInvoicesWithCurrentDate',
            group: 'contacts.contact',
            filters: [
                {
                    label: t('yes'),
                    argument: 'true',
                },
            ],
        },
        relationshipPartner: {
            type: 'dynamicmultiselect',
            group: 'contacts.contact',
            label: t('contacts.about.relationship-partner'),
            selector: 'partners.employee.id',
            canExclude: true,
            paginated: {
                fetch: useLazyGetEmployeesListWithFiltersQuery,
                searchField: 'fullName',
                getOptionIdentifier: 'id',
                getOptionLabel: 'name',
            },
        },
        relationshipManager: {
            type: 'dynamicmultiselect',
            group: 'contacts.contact',
            label: t('contacts.about.relationship-manager'),
            selector: 'managers.employee.id',
            canExclude: true,
            paginated: {
                fetch: useLazyGetEmployeesListWithFiltersQuery,
                searchField: 'fullName',
                getOptionIdentifier: 'id',
                getOptionLabel: 'name',
            },
        },
        clientAssistant: {
            type: 'dynamicmultiselect',
            group: 'contacts.contact',
            label: clientAssistantLabel,
            selector: 'administrators.employee.id',
            canExclude: true,
            paginated: {
                fetch: useLazyGetEmployeesListWithFiltersQuery,
                searchField: 'fullName',
                getOptionIdentifier: 'id',
                getOptionLabel: 'name',
            },
        },
        businessArea: {
            type: 'multiselect',
            label: t('contacts.business-area'),
            selector: 'businessAreas.businessAreaCode',
            group: 'contacts.contact',
            canExclude: true,
            options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['BUSINESS_AREA_TYPE']) : [],
        },
        sectorGroup: {
            type: 'dynamicmultiselect',
            group: 'contacts.contact',
            label: t('contacts.filter.sector-group'),
            tooltip: 'contacts.tooltips.sector-group',
            selector: 'id',
            comperator: '=hasbusinessareasinsectorgroup=',
            canExclude: true,
            paginated: {
                fetch: useLazyGetMinTeamsListWithFiltersQuery,
                searchField: 'name',
                filters: `teamTypeCode==${TeamType.SECTOR_GROUP};isParent==true`,
                getOptionIdentifier: 'id',
                getOptionLabel: 'name',
            },
        },
        segment: {
            type: 'multiselect',
            label: t('contacts.segment-type'),
            selector: 'segmentTypeCode',
            group: 'contacts.contact',
            tooltip: 'contacts.tooltips.segment-filter',
            canExclude: true,
            options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['SEGMENT_TYPE']) : [],
        },
        serviceLevel: {
            type: 'multiselect',
            label: t('contacts.service-level-type'),
            selector: 'serviceLevelTypeCode',
            group: 'contacts.contact',
            tooltip: 'contacts.tooltips.service-level-filter',
            canExclude: true,
            options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['SERVICE_LEVEL_TYPE']) : [],
        },
        employer: {
            type: 'dynamicmultiselect',
            subType: 'contact',
            label: t('contacts.contact-employer'),
            selector: 'id',
            comperator: '=isemployercontact=',
            group: 'contacts.contact',
            tooltip: 'contacts.tooltips.contact-employer',
            canExclude: true,
            paginated: {
                fetch: useLazyGetMinContactsListWithFiltersQuery,
                filters: `partyType==${PartyType.COMPANY}`,
                searchField: ['name', 'partyNumber'],
                getOptionIdentifier: 'id',
                getOptionLabel: 'name',
            },
        },
        country: {
            type: 'multiselect',
            label: t('address-form.country'),
            selector: 'addresses.countryCode',
            group: 'contacts.contact',
            tooltip: 'contacts.tooltips.country',
            canExclude: true,
            options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['COUNTRY']) : [],
        },
        city: {
            type: 'text',
            label: t('address-form.city'),
            selector: 'addresses.cityName',
            group: 'contacts.contact',
            multiple: true,
        },
        casesHandledByOffice: {
            type: 'dynamicmultiselect',
            group: 'contacts.contact',
            label: t('contacts.filter.has-cases-handled-by-office'),
            selector: 'matters.handledByOffice.id',
            tooltip: 'contacts.tooltips.has-cases-handled-by-office',
            canExclude: true,
            paginated: {
                fetch: useLazyGetMinOfficesWithFiltersQuery,
                searchField: 'name',
                getOptionIdentifier: 'id',
                getOptionLabel: 'name',
            },
            getOptionEndAdornment: officeInactiveSuffix,
        },
        noRecentWorkByOffice: {
            type: 'dynamicmultiselect',
            group: 'contacts.contact',
            label: t('contacts.filter.no-recent-work-by-office'),
            selector: 'id',
            comperator: '=norecentworkbyoffice=',
            tooltip: 'contacts.tooltips.no-recent-work-by-office',
            paginated: {
                fetch: useLazyGetMinOfficesWithFiltersQuery,
                searchField: 'name',
                getOptionIdentifier: 'id',
                getOptionLabel: 'name',
            },
            getOptionEndAdornment: officeInactiveSuffix,
        },
        neverServedByOffice: {
            type: 'dynamicmultiselect',
            group: 'contacts.contact',
            label: t('contacts.filter.never-served-by-office'),
            selector: 'id',
            comperator: '=neverservedbyoffice=',
            tooltip: 'contacts.tooltips.never-served-by-office',
            paginated: {
                fetch: useLazyGetMinOfficesWithFiltersQuery,
                searchField: 'name',
                getOptionIdentifier: 'id',
                getOptionLabel: 'name',
            },
            getOptionEndAdornment: officeInactiveSuffix,
        },
        casesInPracticeGroup: {
            type: 'dynamicmultiselect',
            group: 'contacts.contact',
            label: t('contacts.filter.has-cases-in-practice-group'),
            selector: 'matters.practiceAreas.practiceArea.teamPracticeAreas.team.id',
            tooltip: 'contacts.tooltips.has-cases-in-practice-group',
            canExclude: true,
            paginated: {
                fetch: useLazyGetMinTeamsListWithFiltersQuery,
                searchField: 'name',
                filters: `teamTypeCode==${TeamType.PRACTICE_GROUP};isParent==true`,
                getOptionIdentifier: 'id',
                getOptionLabel: 'name',
            },
        },
        noRecentWorkByPracticeGroup: {
            type: 'dynamicmultiselect',
            group: 'contacts.contact',
            label: t('contacts.filter.no-recent-work-by-practice-group'),
            selector: 'id',
            comperator: '=norecentworkbypracticegroup=',
            tooltip: 'contacts.tooltips.no-recent-work-by-practice-group',
            paginated: {
                fetch: useLazyGetMinTeamsListWithFiltersQuery,
                searchField: 'name',
                filters: `teamTypeCode==${TeamType.PRACTICE_GROUP};isParent==true`,
                getOptionIdentifier: 'id',
                getOptionLabel: 'name',
            },
        },
        neverServedByPracticeGroup: {
            type: 'dynamicmultiselect',
            group: 'contacts.contact',
            label: t('contacts.filter.never-served-by-practice-group'),
            selector: 'id',
            comperator: '=neverservedbypracticegroup=',
            tooltip: 'contacts.tooltips.never-served-by-practice-group',
            paginated: {
                fetch: useLazyGetMinTeamsListWithFiltersQuery,
                searchField: 'name',
                filters: `teamTypeCode==${TeamType.PRACTICE_GROUP};isParent==true`,
                getOptionIdentifier: 'id',
                getOptionLabel: 'name',
            },
        },
        memberOfGroup: {
            type: 'dynamicmultiselect',
            group: 'contacts.contact',
            label: t('contacts.filter.member-of-the-group'),
            tooltip: 'contacts.tooltips.member-of-groups',
            selector: 'id',
            comperator: '=ismemberofgroup=',
            canExclude: true,
            paginated: {
                fetch: useLazyGetMinGroupsListWithFiltersQuery,
                searchField: 'groupName',
                getOptionIdentifier: 'id',
                getOptionLabel: 'groupName',
            },
        },
        cooperationPartner: {
            type: 'multiselect',
            label: t('contacts.cooperation-partner'),
            selector: 'cooperationAreaTypes.cooperationAreaTypeCode',
            group: 'contacts.contact',
            tooltip: 'contacts.tooltips.cooperation-partner-filter',
            canExclude: true,
            options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['COOPERATION_AREA_TYPE']) : [],
        },
        keywords: {
            type: 'multiselect',
            label: t('contacts.related-keywords'),
            selector: 'relatedKeywords.keywordTypeCode',
            group: 'contacts.contact',
            tooltip: 'contacts.tooltips.contact-related-keywords-filter',
            canExclude: true,
            options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['KEYWORD_TYPE']) : [],
        },
        notes: {
            type: 'text',
            label: t('contacts.filter.contact-related-notes'),
            selector: 'notes',
            group: 'contacts.contact',
            tooltip: 'contacts.tooltips.contact-related-notes-filter',
        },
        createdDate: {
            type: 'offset-date-time-range',
            group: 'contacts.contact',
            selector: 'createdAt',
            label: t('contacts.filter.created-date-from'),
            labelTo: t('contacts.filter.created-date-to'),
            shouldDisplayQuickPick: false,
        },
        clientSince: {
            type: 'offset-date-time-range',
            group: 'contacts.contact',
            selector: 'effectiveClientSince',
            label: t('contacts.filter.client-since-from'),
            labelTo: t('contacts.filter.client-since-to'),
            tooltip: 'contacts.tooltips.client-since-filter',
            shouldDisplayQuickPick: false,
        },
        caseLastTimecardDate: {
            type: 'offset-date-time-range',
            group: 'contacts.contact',
            selector: 'id',
            comperator: '=caselasttimecarddate=',
            label: t('contacts.case-last-timecard-date-from'),
            labelTo: t('contacts.case-last-timecard-date-to'),
            tooltip: 'contacts.tooltips.case-last-timecard-date-filter',
            shouldDisplayQuickPick: false,
        },
        lastBdEffortFrom: {
            type: 'offset-date-time-range',
            group: 'contacts.contact',
            selector: 'id',
            comperator: '=lastbdeffortdate=',
            label: t('contacts.filter.last-bd-effort-from'),
            labelTo: t('contacts.filter.last-bd-effort-to'),
            tooltip: 'contacts.tooltips.last-bd-effort-filter',
            shouldDisplayQuickPick: false,
        },
        lastBdEffortOlderThan: {
            type: 'date',
            group: 'contacts.contact',
            selector: 'id',
            comperator: '=lastbdeffortolderthan=',
            label: t('contacts.filter.last-bd-effort-older-than'),
            tooltip: 'contacts.tooltips.last-bd-effort-older-than-filter',
        },
        lastEffortActivityFrom: {
            type: 'offset-date-time-range',
            group: 'contacts.contact',
            selector: 'id',
            comperator: '=lasthcsmeetingdate=',
            label: t('contacts.filter.last-effort-activity-from', {
                activityType: activityType,
            }),
            labelTo: t('contacts.filter.last-effort-activity-to', {
                activityType: activityType,
            }),
            tooltip: 'contacts.tooltips.last-hcs-meeting-filter',
            tooltipValues: { activityType: activityType },
            shouldDisplayQuickPick: false,
        },
        lastEffortActivityOlderThan: {
            type: 'date',
            group: 'contacts.contact',
            selector: 'id',
            comperator: '=lasthcsmeetingolderthan=',
            label: t('contacts.filter.last-effort-activity-older-than', {
                activityType: activityType,
            }),
            tooltip: 'contacts.tooltips.last-hcs-meeting-older-than-filter',
            tooltipValues: { activityType: activityType },
        },
        businessContactOf: {
            type: 'dynamicmultiselect',
            group: 'contacts.person-filters.title',
            label: t('contacts.person-filters.business-contact-of'),
            selector: 'id',
            comperator: '=hasbusinesscontactof=',
            tooltip: 'contacts.tooltips.business-contact-of-filter',
            canExclude: true,
            paginated: {
                fetch: useLazyGetAllEmployeesListWithFiltersQuery,
                searchField: 'fullName',
                getOptionIdentifier: 'id',
                getOptionLabel: 'name',
            },
        },
        mailingPreferences: {
            type: 'multiselect',
            label: t('contacts.person-filters.mailing-preferences'),
            selector: 'mailingPreferences.mailingPreferenceTypeCode',
            group: 'contacts.person-filters.title',
            tooltip: 'contacts.tooltips.mailing-preferences-filter',
            canExclude: true,
            options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['MAILING_PREFERENCE_TYPE']) : [],
        },
        marketingAreas: {
            type: 'multiselect',
            label: t('contacts.person-filters.preferred-marketing-areas'),
            selector: 'marketingAreaTypes.marketingAreaTypeCode',
            group: 'contacts.person-filters.title',
            tooltip: 'contacts.tooltips.preferred-marketing-areas-filter',
            canExclude: true,
            options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['MARKETING_AREA_TYPE']) : [],
        },
        preferredMailingLanguage: {
            type: 'multiselect',
            label: t('contacts.person-filters.preferred-mailing-language'),
            selector: 'preferredMailingLanguageTypeCode',
            group: 'contacts.person-filters.title',
            tooltip: 'contacts.tooltips.preferred-mailing-language-filter',
            options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['LANGUAGE']) : [],
        },
        jurisdictions: {
            type: 'multiselect',
            label: t('contacts.person-filters.jurisdictions-of-interest'),
            selector: 'countries.countryTypeCode',
            group: 'contacts.person-filters.title',
            tooltip: 'contacts.tooltips.jurisdictions-of-interest-filter',
            canExclude: true,
            options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['COUNTRY']) : [],
        },
        employerCountry: {
            type: 'multiselect',
            label: t('contacts.person-filters.employer-country'),
            selector: 'id',
            comperator: '=hasemployercountry=',
            group: 'contacts.person-filters.title',
            tooltip: 'contacts.tooltips.employer-country-filter',
            canExclude: true,
            options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['COUNTRY']) : [],
        },
        employerBusinessArea: {
            type: 'multiselect',
            label: t('contacts.person-filters.employer-business-area'),
            selector: 'id',
            comperator: '=hasemployerbusinessarea=',
            group: 'contacts.person-filters.title',
            tooltip: 'contacts.tooltips.employer-business-area-filter',
            canExclude: true,
            options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['BUSINESS_AREA_TYPE']) : [],
        },
        employerSegment: {
            type: 'multiselect',
            label: t('contacts.person-filters.employer-segment'),
            selector: 'id',
            comperator: '=hasemployersegment=',
            group: 'contacts.person-filters.title',
            tooltip: 'contacts.tooltips.employer-segment-filter',
            canExclude: true,
            options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['SEGMENT_TYPE']) : [],
        },
        employerServiceLevel: {
            type: 'multiselect',
            label: t('contacts.person-filters.employer-service-level'),
            selector: 'id',
            comperator: '=hasemployerservicelevel=',
            group: 'contacts.person-filters.title',
            tooltip: 'contacts.tooltips.employer-service-level-filter',
            canExclude: true,
            options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['SERVICE_LEVEL_TYPE']) : [],
        },
        employerKeyword: {
            type: 'multiselect',
            label: t('contacts.person-filters.employer-related-keyword'),
            selector: 'id',
            comperator: '=hasemployerkeyword=',
            group: 'contacts.person-filters.title',
            tooltip: 'contacts.tooltips.employer-related-keyword-filter',
            canExclude: true,
            options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['KEYWORD_TYPE']) : [],
        },
        position: {
            type: 'text',
            label: t('contacts.person-filters.position'),
            selector: 'id',
            comperator: '=positionlk=',
            group: 'contacts.person-filters.title',
            tooltip: 'contacts.tooltips.position-filter',
            multiple: true,
            // Positions commonly contain commas ("Head of Legal, Baltics"), so terms are
            // separated with Enter instead.
            commitOnEnter: true,
        },
        areasOfResponsibility: {
            type: 'multiselect',
            label: t('contacts.person-filters.areas-of-responsibility'),
            selector: 'areaOfResponsibilityTypes.areaOfResponsibilityTypeCode',
            group: 'contacts.person-filters.title',
            tooltip: 'contacts.tooltips.areas-of-responsibility-filter',
            canExclude: true,
            // Alphabetical, as in the field's picker: the values are each firm's own, in the order added.
            options: classifiers
                ? getFilterOptionsFromClassifier(classifiers['AREA_OF_RESPONSIBILITY_TYPE']).toSorted((a, b) =>
                      a.label.localeCompare(b.label)
                  )
                : [],
        },
        excludeEmployedByInternalClient: {
            type: 'checkboxgroup',
            selector: 'id',
            comperator: '=excludeemployedbyinternalclient=',
            group: 'contacts.person-filters.title',
            filters: [
                {
                    tooltip: 'contacts.tooltips.exclude-employed-by-internal-clients',
                    label: t('contacts.person-filters.exclude-employed-by-internal-clients'),
                    argument: 'true',
                },
            ],
        },
        excludePersonaNonGrata: {
            type: 'checkboxgroup',
            selector: 'id',
            comperator: '=excludepersonanongrata=',
            group: 'contacts.person-filters.title',
            filters: [
                {
                    tooltip: 'contacts.tooltips.exclude-persona-non-grata',
                    label: t('contacts.person-filters.exclude-persona-non-grata'),
                    argument: 'true',
                },
            ],
        },
        caseStatus: {
            type: 'multiselect',
            label: t('contacts.filter.connected-case-status'),
            group: 'contacts.filter.connected-cases',
            selector: 'matters.matterStateTypeCode',
            options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['MATTER_STATE_TYPE']) : [],
        },
        caseSupervisingPartner: {
            type: 'dynamicmultiselect',
            group: 'contacts.filter.connected-cases',
            label: t('contacts.filter.case-supervising-partner'),
            selector: 'matters.supervisedByEmployee.id',
            canExclude: true,
            paginated: {
                fetch: useLazyGetEmployeesListWithFiltersQuery,
                searchField: 'fullName',
                getOptionIdentifier: 'id',
                getOptionLabel: 'name',
            },
        },
        caseResponsibleLawyer: {
            type: 'dynamicmultiselect',
            group: 'contacts.filter.connected-cases',
            label: t('contacts.filter.case-responsible-lawyer'),
            selector: 'matters.responsibleEmployee.id',
            canExclude: true,
            paginated: {
                fetch: useLazyGetEmployeesListWithFiltersQuery,
                searchField: 'fullName',
                getOptionIdentifier: 'id',
                getOptionLabel: 'name',
            },
        },
        caseOpeningDate: {
            type: 'daterange',
            group: 'contacts.filter.connected-cases',
            selector: 'matters.openedDate',
            label: t('contacts.filter.case-opening-date-from'),
            labelTo: t('contacts.filter.case-opening-date-to'),
            shouldDisplayQuickPick: false,
        },
        opponent: {
            type: 'checkboxgroup',
            label: t('contacts.filter.opponent'),
            selector: 'id',
            comperator: '=isopponent=',
            group: 'contacts.filter.connected-cases',
            multiple: true,
            filters: [
                {
                    label: t('contacts.filter.opponent-in-ongoing-case'),
                    argument: 'true',
                },
                {
                    label: t('contacts.filter.opponent-in-completed-case'),
                    argument: 'COMPLETED',
                },
            ],
        },
        selectedAsJudge: {
            type: 'checkboxgroup',
            selector: 'id',
            comperator: '=selectedasjudge=',
            group: 'contacts.other.title',
            filters: [
                {
                    tooltip: 'contacts.tooltips.selected-as-judge-filter',
                    label: t('contacts.selected-as-judge'),
                    argument: 'true',
                },
            ],
        },
        activeCasesNoAgreement: {
            type: 'checkboxgroup',
            selector: 'id',
            comperator: '=hasactivecasesnoagreement=',
            group: 'contacts.other.title',
            filters: [
                {
                    tooltip: 'contacts.tooltips.active-cases-no-agreement',
                    label: t('contacts.filter.active-cases-no-agreement'),
                    argument: 'true',
                },
            ],
        },
        inactiveRelationshipPartner: {
            type: 'checkboxgroup',
            selector: 'partners.employee.isActive',
            group: 'contacts.other.title',
            filters: [
                {
                    tooltip: 'contacts.tooltips.inactive-relationship-partner',
                    label: t('contacts.filter.inactive-relationship-partner'),
                    argument: 'false',
                },
            ],
        },
        inactiveRelationshipManager: {
            type: 'checkboxgroup',
            selector: 'managers.employee.isActive',
            group: 'contacts.other.title',
            filters: [
                {
                    tooltip: 'contacts.tooltips.inactive-relationship-manager',
                    label: t('contacts.filter.inactive-relationship-manager'),
                    argument: 'false',
                },
            ],
        },
        addingCasesBlocked: {
            type: 'checkboxgroup',
            selector: 'isAddingNewCasesBlocked',
            group: 'contacts.other.title',
            filters: [
                {
                    tooltip: 'contacts.tooltips.adding-cases-blocked',
                    label: t('contacts.filter.adding-cases-blocked'),
                    argument: 'true',
                },
            ],
        },
        addingTimecardsBlocked: {
            type: 'checkboxgroup',
            selector: 'isAddingTimecardsBlocked',
            group: 'contacts.other.title',
            filters: [
                {
                    tooltip: 'contacts.tooltips.adding-timecards-blocked',
                    label: t('contacts.filter.adding-timecards-blocked'),
                    argument: 'true',
                },
            ],
        },
    }) satisfies Record<string, MainFilter>;
